/**
 * Safe localStorage wrapper that handles SecurityError
 * when localStorage is blocked by browser settings
 */

const memoryStorage = {};

const isStorageAvailable = () => {
    if (typeof window === 'undefined') return false;
    try {
        const testKey = '__storage_test__';
        window.localStorage.setItem(testKey, testKey);
        window.localStorage.removeItem(testKey);
        return true;
    } catch (e) {
        return false;
    }
};

let storageAvailable = null;

const checkStorage = () => {
    if (storageAvailable === null) {
        storageAvailable = isStorageAvailable();
    }
    return storageAvailable;
};

export const safeGetItem = (key) => {
    if (typeof window === 'undefined') return null;
    try {
        if (checkStorage()) {
            return window.localStorage.getItem(key);
        }
        return memoryStorage[key] || null;
    } catch (e) {
        return memoryStorage[key] || null;
    }
};

export const safeSetItem = (key, value) => {
    if (typeof window === 'undefined') return;
    try {
        if (checkStorage()) {
            window.localStorage.setItem(key, value);
        } else {
            memoryStorage[key] = value;
        }
    } catch (e) {
        memoryStorage[key] = value;
    }
};

export const safeRemoveItem = (key) => {
    if (typeof window === 'undefined') return;
    try {
        if (checkStorage()) {
            window.localStorage.removeItem(key);
        }
        delete memoryStorage[key];
    } catch (e) {
        delete memoryStorage[key];
    }
};

// Storage object compatible with localStorage API (for libraries like apollo-cache-persist)
export const safeLocalStorage = {
    getItem: safeGetItem,
    setItem: safeSetItem,
    removeItem: safeRemoveItem,
    clear: () => {
        if (typeof window === 'undefined') return;
        try {
            if (checkStorage()) {
                window.localStorage.clear();
            }
            Object.keys(memoryStorage).forEach(key => delete memoryStorage[key]);
        } catch (e) {
            Object.keys(memoryStorage).forEach(key => delete memoryStorage[key]);
        }
    },
    get length() {
        if (typeof window === 'undefined') return 0;
        try {
            if (checkStorage()) {
                return window.localStorage.length;
            }
            return Object.keys(memoryStorage).length;
        } catch (e) {
            return Object.keys(memoryStorage).length;
        }
    },
    key: (index) => {
        if (typeof window === 'undefined') return null;
        try {
            if (checkStorage()) {
                return window.localStorage.key(index);
            }
            return Object.keys(memoryStorage)[index] || null;
        } catch (e) {
            return Object.keys(memoryStorage)[index] || null;
        }
    },
};

export default safeLocalStorage;
